/* eslint-disable @typescript-eslint/no-explicit-any */
import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Configure tailwind-merge to recognize custom typography classes so that they are merged correctly.
 * Without this, any of the custom typography classes would be overridden by other `text-*` classes.
 * @example 'text-h1 text-red' -> 'text-red' // not what we want
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        'text-h1',
        'text-h2',
        'text-h3',
        'text-h4',
        'text-h5',
        'text-h6',
        'text-caption-read-lg',
        'text-caption-read',
        'text-caption-action-lg',
        'text-caption-action'
      ]
    }
  }
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Will get the value of a CSS variable specified on `:root`.
 * This should only be used in cases where you have to programattically provide a concrete value
 * rather than applying a tailwind classname. i.e. some 3rd party libraries
 * @param variable The CSS variable name (inc. -- prefix)
 * @example
 * getCssVariable('--cxcentral-green') // "#0ea2a7"
 * getCssVariable('--doesnt-exist')    // undefined
 */
export function getCSSVariable(variable: string): string | undefined {
  return window.getComputedStyle(document.documentElement)?.getPropertyValue(variable);
}

export function normalizeLinksInHtml(html: string): string {
  if (!html) return '';

  const container = document.createElement('div');
  container.innerHTML = html;

  const anchors = Array.from(container.querySelectorAll('a')) as HTMLAnchorElement[];
  for (const anchor of anchors) {
    const currentHref = (anchor.getAttribute('href') || '').trim();
    const anchorText = (anchor.textContent || '').trim();

    const normalized = normalizeToAllowedUrl(currentHref || anchorText);

    if (normalized) {
      anchor.setAttribute('href', normalized);
      if (!anchor.getAttribute('target')) anchor.setAttribute('target', '_blank');
      if (!anchor.getAttribute('rel')) anchor.setAttribute('rel', 'noreferrer');
    } else {
      anchor.removeAttribute('href');
    }
  }

  return container.innerHTML;
}

function normalizeToAllowedUrl(raw: string): string | null {
  if (!raw) return null;
  const value = raw.trim();

  // Disallow clearly unsafe protocols
  if (/^(javascript|data|vbscript):/i.test(value)) return null;

  // Already acceptable absolute links
  if (/^https?:\/\//i.test(value)) return value;
  if (/^mailto:/i.test(value)) return value;
  if (/^tel:/i.test(value)) return value;

  // Bare email
  if (/^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/i.test(value)) {
    return `mailto:${value}`;
  }

  // Starts with www.
  if (/^www\./i.test(value)) {
    return `https://${value}`;
  }

  // Bare domain like example.com or example.co.uk
  if (/^[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?:\/\S*)?$/i.test(value)) {
    return `https://${value}`;
  }

  return null;
}

export const formatUserName = (name: string) => {
  return name
    .replace(/\./g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
